import type { Annotation } from "building-types/annotation/annotationTypes";
import { PositioningMethod } from "building-types/annotation/annotationTypesEnum";
import type { Building } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import type { AnnotationHandle, ContentTemplateInstanceHandle } from "building-types/handles/docEntityHandleTypes";
import type { LevelDef } from "building-types/levels/LevelDef";
import type { Formula } from "building-types/options/formula";
import { EntityInstanceType } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { RoomLayout } from "building-types/roomLayouts/roomLayoutTypes";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { ElevationReferenceFrame } from "building-types/views/elevationReferenceFrame";
import type { ContentTemplate, ContentTemplateInstance, DrawingTemplateInstance } from "building-types/views/planSets";
import type { ElevationView, SectionView } from "building-types/views/View3D";
import type { RWTable, Table } from "common/data/table";
import objectIsEmpty from "common/utils/objectIsEmpty";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import { evalFormulas, resolveOptions } from "doc/formula/evalFormulas";
import { docToOptionDefinitions } from "doc/options/docGetInstanceOptionSets";
import {
  entityHandlesIfRoomLayoutInstanceOverride,
  buildRoomLayoutInstancesByRoomLayoutId,
  entityInstancesForEntity,
} from "doc/rooms/layout/roomLayoutTools";
import { optionChoiceParameterValues } from "doc/systemParameterTools";
import { evaluateContentTemplateInstance } from "../contentTemplates/planSetsEvaluate";
import type { ContentTemplateInstanceNode } from "../contentTemplates/planSetsEvaluate";
import { resolveEntityHostedAnnotationTableArray } from "../elements/entityHostedAnnotationResolver";
import type { BuildingForEntityHostedAnnotations } from "../elements/entityHostedAnnotationResolver";
import {
  addComponentAnnotations,
  addComponentDrawings,
  type ComponentInstancingContext,
} from "../generationTools/components/componentAnnotations";
import {
  addDrawingTemplateInstanceAnnotation,
  annotationTransformToGlobal,
  generateLevelAnnotation,
  mirrorAnnotation,
  elevationViewMirrorParameters,
  mirrorDrawingTemplateInstance,
  drawingTemplateInstanceTransformToGlobal,
} from "../generationTools/generateAnnotationTools";
import { isCombinedGlobalInstanceMirror } from "../generationTools/generateOpeningsTools";
import { sectionViewAnnotationMirrorTranslationX } from "../generationTools/sectionViewClippingPlane";
import { isRoomLayoutInstanceEnabledForChain } from "../utils/componentInstanceUtils";
import { shouldGenerateEntity } from "../utils/entityIsolation";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

export function selectAnnotation({
  doc,
  doc: {
    annotation,
    roomLayouts,
    roomLayoutInstances,
    entityRoomLayouts,
    contentTemplates,
    contentTemplateInstances,
    sectionViews,
    elevationViews,
    rooms: docRooms,
    assets,
    levels: docLevels,
    optionSets,
    optionChoices: docOptionChoices,
    instanceOptionSets,
  },
  referenceGeom,
  idToParents,
  componentToTransforms,
  enabledFeatures,
  isolatedEntity,
  optionChoices,
  globalParamValues,
  mirror,
  mirrorTranslationX,
  rooms,
  roomIndex,
  wallMasses,
  levels,
  settings,
  entityDisabledByGroup,
}: Pick<
  Building,
  | "doc"
  | "referenceGeom"
  | "idToParents"
  | "componentToTransforms"
  | "enabledFeatures"
  | "isolatedEntity"
  | "optionChoices"
  | "globalParamValues"
  | "mirror"
  | "mirrorTranslationX"
  | "rooms"
  | "roomIndex"
  | "wallMasses"
  | "levels"
  | "settings"
  | "entityDisabledByGroup"
>) {
  const definitions = docToOptionDefinitions(doc);
  return {
    doc,
    referenceGeom,
    roomLayouts,
    entityRoomLayouts,
    contentTemplates,
    contentTemplateInstances,
    sectionViews,
    elevationViews,
    docAnnotation: annotation,
    rooms,
    docRooms,
    roomIndex,
    wallMasses,
    roomLayoutInstances,
    levels,
    mirror,
    wallSettings: settings.wall,
    mirrorTranslationX,
    optionChoices: resolveOptions(definitions, optionChoices), // Resolve all option choices, it's non-trivial to filter based on drawing template instances.
    globalParamValues,
    planSettings: settings.planset,
    entityDisabledByGroup,
    assetCatalog: assets,
    docLevels,
    idToParents,
    componentToTransforms,
    enabledFeatures,
    isolatedEntity,
    optionSets,
    docOptionChoices,
    instanceOptionSets,
  };
}

const compare = makeGenerationStageComparator<typeof selectAnnotation>({
  docRooms: "shallow",
  optionChoices: "shallow",
  idToParents: "shallow",
  componentToTransforms: "shallow",
  enabledFeatures: "shallow",
  isolatedEntity: "shallow",
});

type GenerateAnnotationParams = ReturnType<typeof selectAnnotation>;

type AnnotationParentTable = Table<
  RoomLayout | ContentTemplate | ContentTemplateInstance | LevelDef | ElevationView | SectionView | StaticRoom
>;
type AnnotationParent = AnnotationParentTable[keyof AnnotationParentTable];
interface AnnotationParentTables {
  roomLayouts: AnnotationParentTable;
  contentTemplates: AnnotationParentTable;
  contentTemplateInstances: AnnotationParentTable;
  sectionViews: AnnotationParentTable;
  elevationViews: AnnotationParentTable;
  docRooms: AnnotationParentTable;
  docLevels: AnnotationParentTable;
}

interface AnnotationParentLookup {
  readonly parentByParentId: Table<AnnotationParent>;
  readonly roomLayoutParentByEntityHash: Table<AnnotationParent>;
  readonly roomLayouts: AnnotationParentTable;
}

function buildAnnotationParentLookup(
  annotationParentTables: AnnotationParentTables,
  entityRoomLayouts: Doc["entityRoomLayouts"],
): AnnotationParentLookup {
  const { roomLayouts, ...parentTablesByParentId } = annotationParentTables;
  const parentByParentId: RWTable<AnnotationParent> = {};
  for (const table of Object.values(parentTablesByParentId)) {
    for (const id in table) {
      if (!Object.hasOwn(parentByParentId, id)) {
        parentByParentId[id] = table[id];
      }
    }
  }

  const roomLayoutParentByEntityHash: RWTable<AnnotationParent> = {};
  for (const hash in entityRoomLayouts) {
    const parent = roomLayouts[entityRoomLayouts[hash]];
    if (parent) {
      roomLayoutParentByEntityHash[hash] = parent;
    }
  }

  return { parentByParentId, roomLayoutParentByEntityHash, roomLayouts };
}

function getAnnotationParentFromLookup(
  annotation: Annotation | ContentTemplateInstance,
  handle: AnnotationHandle | ContentTemplateInstanceHandle,
  lookup: AnnotationParentLookup,
): AnnotationParent | undefined {
  if (!annotation.parentId) {
    return undefined;
  }
  const hash = docEntityHandleHash(handle);
  const roomLayoutParent = lookup.roomLayoutParentByEntityHash[hash];
  if (roomLayoutParent) {
    return roomLayoutParent;
  }
  const { parentId } = annotation;
  return lookup.roomLayouts[parentId] ?? lookup.parentByParentId[parentId];
}

export function generateAnnotation(
  {
    doc,
    docAnnotation,
    referenceGeom,
    roomLayouts,
    entityRoomLayouts,
    contentTemplates,
    contentTemplateInstances,
    sectionViews,
    elevationViews,
    optionChoices,
    globalParamValues,
    mirror,
    mirrorTranslationX,
    wallSettings,
    roomLayoutInstances,
    rooms,
    docRooms,
    docLevels,
    roomIndex,
    wallMasses,
    levels,
    planSettings,
    entityDisabledByGroup,
    idToParents,
    componentToTransforms,
    enabledFeatures,
    isolatedEntity,
    assetCatalog,
    optionSets,
    docOptionChoices,
    instanceOptionSets,
  }: GenerateAnnotationParams,
  forEveryAnnotation:
    | ((annotationId: string, annotation: Annotation, disabled?: boolean | Formula) => void)
    | null = null,
): Pick<
  Building,
  | "roomAnnotation"
  | "levelAnnotation"
  | "elevationDrawingAnnotation"
  | "sectionViewAnnotation"
  | "annotation"
  | "modelHostedDrawings"
  | "issues"
> {
  const issues: BuildingGenerationIssue[] = [];
  const roomAnnotation: { [roomId: string]: Annotation[] } = {};
  const userDefinedLevelAnnotation: { [levelId: string]: Annotation[] } = {};
  const elevationDrawingAnnotation: {
    [key in ElevationReferenceFrame]: Annotation[];
  } = {
    FRONT: [],
    BACK: [],
    LEFT: [],
    RIGHT: [],
  };
  const sectionViewAnnotation: { [viewId: string]: Annotation[] } = {};
  const contentTemplateAnnotation: { [templateId: string]: Annotation[] } = {};
  const contentTemplateInstanceAnnotation: { [instanceId: string]: Annotation[] } = {};
  const enabledContentTemplateInstances: RWTable<ContentTemplateInstanceHandle> = {};
  const wallLayerExtentsCache: WallLayerExtentsCache = {};

  const annotationParentTables: AnnotationParentTables = {
    roomLayouts,
    contentTemplates,
    contentTemplateInstances,
    sectionViews,
    elevationViews,
    /**
     * It is important to note that these are the rooms that live on the doc
     * and have thus not been evaluated for their disabled state. We use this
     * table to correctly derive the parent of annotations, but we will
     * bail on generating annotations for rooms that are disabled below.
     */
    docRooms,
    docLevels,
  } as const;

  const annotationParentLookup = buildAnnotationParentLookup(annotationParentTables, entityRoomLayouts);
  const hasRoomLayouts = !objectIsEmpty(entityRoomLayouts);
  const roomLayoutInstancesByRoomLayoutId = hasRoomLayouts
    ? buildRoomLayoutInstancesByRoomLayoutId(roomLayoutInstances)
    : undefined;
  const roomLayoutInstancingOptions = {
    hasRoomLayouts,
    docEntityRoomLayouts: entityRoomLayouts,
    docRoomLayouts: roomLayouts,
    rooms: docRooms,
    activeRoomLayoutInstances: roomLayoutInstances,
    roomLayoutInstancesByRoomLayoutId,
  } as const;

  const sectionViewMirrorTranslationXCache: { [sectionViewId: string]: number } = {};
  function getSectionViewMirrorTranslationX(
    sectionViewId: string,
    mirror: boolean,
    mirrorTranslationX: number,
  ): number {
    if (!mirror) {
      return 0;
    }
    if (sectionViewMirrorTranslationXCache.hasOwnProperty(sectionViewId)) {
      return sectionViewMirrorTranslationXCache[sectionViewId];
    }
    const sectionView = sectionViews[sectionViewId];
    const sectionViewMirrorTranslationX = sectionViewAnnotationMirrorTranslationX(
      sectionView,
      mirror,
      mirrorTranslationX,
    );
    sectionViewMirrorTranslationXCache[sectionViewId] = sectionViewMirrorTranslationX;
    return sectionViewMirrorTranslationX;
  }

  const instancingContext: ComponentInstancingContext = {
    doc: {
      optionSets,
      instanceOptionSets,
    },
    optionChoices,
    mirror,
    mirrorTranslationX,
    assetCatalog,
    idToParents,
    componentToTransforms,
    rooms,
    isolatedEntity,
    issues,
    referenceGeom,
    roomLayoutInstances,
  };

  for (const annotationId in docAnnotation) {
    const rawAnnotation = docAnnotation[annotationId];
    const handle: AnnotationHandle = { type: "ANNOTATION", id: annotationId };

    const parent: AnnotationParent | undefined = getAnnotationParentFromLookup(
      rawAnnotation,
      handle,
      annotationParentLookup,
    );

    if (!parent) {
      issues.push({
        type: BuildingGenerationIssueType.ANNOTATION_MISSING_PARENT,
        handle,
      });
      continue;
    }

    // Archetype evaluation only. Component instances re-evaluate the raw annotation with
    // their instance option choices in addComponentAnnotations (openings-style).
    const annotation = evalFormulas(rawAnnotation, (id) => optionChoices[id], undefined, entityDisabledByGroup);
    const skipArchetypePlacement = !!annotation.disabled && !forEveryAnnotation;

    // Add the annotation to its respective annotation table
    switch (parent.entityType) {
      case "LEVEL": {
        if (!docLevels[parent.id]) {
          break;
        }

        if (shouldGenerateEntity(parent.id, idToParents, isolatedEntity)) {
          const mirroredAnnotation = mirrorAnnotation({
            mirror,
            mirrorTranslationX,
            referenceGeom,
            annotation,
            assetCatalog,
            handle,
          });

          if (mirroredAnnotation && !skipArchetypePlacement) {
            userDefinedLevelAnnotation[parent.id] = userDefinedLevelAnnotation[parent.id] || [];
            userDefinedLevelAnnotation[parent.id].push(mirroredAnnotation);
          }
        }

        if (enabledFeatures.modelComponents) {
          addComponentAnnotations({
            annotation: rawAnnotation,
            target: {
              type: "LEVEL",
              sourceId: parent.id,
              levels,
              annotationTable: userDefinedLevelAnnotation,
            },
            instancingContext,
            forEveryAnnotation,
          });
        }
        break;
      }
      case "ELEVATION_VIEW": {
        const {
          mirror: adjustedMirror,
          mirrorTranslationX: adjustedMirrorTranslationX,
          mirroredReferenceFrame,
        } = elevationViewMirrorParameters(parent.referenceFrame, mirror, mirrorTranslationX);
        const mirroredAnnotation = mirrorAnnotation({
          annotation,
          mirror: adjustedMirror,
          mirrorTranslationX: adjustedMirrorTranslationX,
          referenceGeom,
          assetCatalog,
          handle,
        });
        if (!mirroredAnnotation || skipArchetypePlacement) {
          break;
        }
        elevationDrawingAnnotation[mirroredReferenceFrame] = elevationDrawingAnnotation[mirroredReferenceFrame] || [];
        elevationDrawingAnnotation[mirroredReferenceFrame].push(mirroredAnnotation);
        break;
      }
      case "SECTION_VIEW": {
        const sectionMirrorTranslationX = getSectionViewMirrorTranslationX(parent.id, mirror, mirrorTranslationX);
        const mirroredAnnotation = mirrorAnnotation({
          annotation,
          mirror,
          mirrorTranslationX: sectionMirrorTranslationX,
          referenceGeom,
          assetCatalog,
          handle,
        });
        if (!mirroredAnnotation || skipArchetypePlacement) {
          break;
        }
        sectionViewAnnotation[parent.id] = sectionViewAnnotation[parent.id] || [];
        sectionViewAnnotation[parent.id].push(mirroredAnnotation);
        break;
      }
      case "ROOM_LAYOUT":
      case "ROOM": {
        if (!docRooms[parent.id] && !roomLayouts[parent.id]) {
          break;
        }

        let annotationParentId = parent.id;
        if (parent.entityType === "ROOM_LAYOUT") {
          annotationParentId = parent.parentId;
        }

        const mirroredAnnotation = mirrorAnnotation({
          mirror,
          mirrorTranslationX: annotation.positioningMethod === PositioningMethod.EDGE ? 0 : mirrorTranslationX,
          referenceGeom,
          annotation,
          assetCatalog,
          handle,
        });

        if (!mirroredAnnotation) {
          break;
        }

        const isGlobal = annotation.positioningMethod === PositioningMethod.GLOBAL;
        const annotationInstances = isGlobal
          ? [{ parentId: annotationParentId, entityInstanceType: undefined, mirrored: false }]
          : entityInstancesForEntity({
              ...roomLayoutInstancingOptions,
              entity: mirroredAnnotation,
            });

        for (const instance of annotationInstances) {
          const annotationHandle: AnnotationHandle = { type: "ANNOTATION", id: annotation.id };
          if (!isGlobal && !instance.edgeId) {
            issues.push({
              type: BuildingGenerationIssueType.INVALID_ANNOTATION_EDGE_ID,
              handle: annotationHandle,
              edgeId: "MISSING",
            });
            continue;
          }

          if (!instance.parentId) {
            continue;
          }

          const rliEnabledForArchetype = isRoomLayoutInstanceEnabledForChain(
            instance.handleOverride,
            [],
            roomLayoutInstances,
            { optionSets, instanceOptionSets },
            optionChoices,
          );

          const instancedRoom = rooms[instance.parentId];

          const { handle, handleAuxiliary } = entityHandlesIfRoomLayoutInstanceOverride(
            annotationHandle,
            instance.handleOverride,
          );

          const id =
            instance.entityInstanceType === EntityInstanceType.ROOM_LAYOUT_INSTANCE
              ? `${instance.parentId}::${annotation.id}`
              : annotation.id;
          let annotationInstance: Annotation = {
            ...mirroredAnnotation,
            parentId: instance.parentId,
            edgeId: instance.edgeId ?? undefined,
            id,
            handle,
            handleAuxiliary,
            entityInstanceType: instance.entityInstanceType,
          };

          if (instancedRoom && rliEnabledForArchetype) {
            if (!isGlobal) {
              annotationInstance = annotationTransformToGlobal(
                annotationInstance,
                instancedRoom,
                isCombinedGlobalInstanceMirror(mirror, instancedRoom),
                issues,
              );
            }

            if (annotationInstance.parentId && !skipArchetypePlacement) {
              roomAnnotation[annotationInstance.parentId] = roomAnnotation[annotationInstance.parentId] || [];
              roomAnnotation[annotationInstance.parentId].push(annotationInstance);
              forEveryAnnotation?.(annotation.id, annotationInstance, annotationInstance.disabled);
            }
          }

          if (enabledFeatures.modelComponents) {
            addComponentAnnotations({
              annotation: rawAnnotation,
              target: {
                type: "ROOM",
                sourceId: instance.parentId,
                placement: {
                  edgeId: instance.edgeId ?? undefined,
                  handleOverride: instance.handleOverride,
                  sourceEntityInstanceType: instance.entityInstanceType ?? EntityInstanceType.ROOM_LAYOUT_SOURCE,
                },
                rooms,
                annotationTable: roomAnnotation,
              },
              instancingContext,
              forEveryAnnotation,
            });
          }
        }

        break;
      }
      case "CONTENT_TEMPLATE":
        if (skipArchetypePlacement) {
          break;
        }
        contentTemplateAnnotation[parent.id] = contentTemplateAnnotation[parent.id] || [];
        contentTemplateAnnotation[parent.id].push({ ...annotation, handle });
        break;
      case "CONTENT_TEMPLATE_INSTANCE":
        if (skipArchetypePlacement) {
          break;
        }
        contentTemplateInstanceAnnotation[parent.id] = contentTemplateInstanceAnnotation[parent.id] || [];
        contentTemplateInstanceAnnotation[parent.id].push({ ...annotation, handle });
        break;
      default:
        break;
    }
  }

  // Ensure globalParamValues reflects the active option choices so that
  // option-set-based disabled formulas on drawing template instances
  // (e.g. migrated annotation catalog conditions) evaluate correctly.
  const resolvedGlobalParamValues = {
    ...globalParamValues,
    ...optionChoiceParameterValues({ optionSets, optionChoices: docOptionChoices }, optionChoices),
  };

  for (const drawingInstanceId in contentTemplateInstances) {
    const drawing = contentTemplateInstances[drawingInstanceId];
    if (drawing.templateType !== "DRAWING") {
      continue;
    }

    if (entityDisabledByGroup[drawingInstanceId]) {
      continue;
    }

    const handle: ContentTemplateInstanceHandle = { type: "CONTENT_TEMPLATE_INSTANCE", id: drawingInstanceId };
    const parent = getAnnotationParentFromLookup(drawing, handle, annotationParentLookup);
    if (!parent) {
      issues.push({
        type: BuildingGenerationIssueType.ANNOTATION_MISSING_PARENT,
        handle,
      });
      continue;
    }

    // Skip evaluation of non-model-hosted content template instances
    if (parent.entityType === "CONTENT_TEMPLATE" || parent.entityType === "CONTENT_TEMPLATE_INSTANCE") {
      continue;
    }

    // Evaluation of the drawing is costly. This allows lazy evaluation and also caching of the result.
    // Archetype evaluation only — component instances re-check disabled via
    // evaluateEntityForComponentInstance on the raw drawing in addComponentDrawings.
    const instanceNode = (() => {
      let cached: ContentTemplateInstanceNode | undefined;
      return () => {
        if (cached === undefined) {
          cached = evaluateContentTemplateInstance({
            instance: drawing,
            doc,
            globalParamValues: resolvedGlobalParamValues,
            userDefinedOptionChoices: optionChoices,
          });
        }
        return cached;
      };
    })();

    const skipArchetypePlacement = !!instanceNode().instance.disabled;

    if (!skipArchetypePlacement) {
      enabledContentTemplateInstances[drawingInstanceId] = handle;
    }

    switch (parent.entityType) {
      case "LEVEL": {
        if (!docLevels[parent.id]) {
          break;
        }

        if (!skipArchetypePlacement && shouldGenerateEntity(parent.id, idToParents, isolatedEntity)) {
          const mirroredDrawing = mirrorDrawingTemplateInstance({
            mirror,
            mirrorTranslationX,
            drawing,
          });

          addDrawingTemplateInstanceAnnotation(
            contentTemplateInstances,
            mirroredDrawing,
            instanceNode(),
            parent.id,
            handle,
            undefined,
            undefined,
            contentTemplateAnnotation,
            mirror,
            assetCatalog,
            userDefinedLevelAnnotation,
          );
        }

        if (enabledFeatures.modelComponents) {
          addComponentDrawings({
            drawing,
            target: {
              type: "LEVEL",
              sourceId: parent.id,
              levels,
              annotationTable: userDefinedLevelAnnotation,
            },
            instancingContext,
            instanceNode,
            contentTemplateInstances,
            contentTemplateAnnotation,
          });
        }
        break;
      }
      case "ELEVATION_VIEW": {
        const {
          mirror: adjustedMirror,
          mirrorTranslationX: adjustedMirrorTranslationX,
          mirroredReferenceFrame,
        } = elevationViewMirrorParameters(parent.referenceFrame, mirror, mirrorTranslationX);
        const mirroredDrawing = mirrorDrawingTemplateInstance({
          drawing,
          mirror: adjustedMirror,
          mirrorTranslationX: adjustedMirrorTranslationX,
        });
        if (!mirroredDrawing || skipArchetypePlacement) {
          break;
        }
        addDrawingTemplateInstanceAnnotation(
          contentTemplateInstances,
          mirroredDrawing,
          instanceNode(),
          mirroredReferenceFrame,
          handle,
          undefined,
          undefined,
          contentTemplateAnnotation,
          adjustedMirror,
          assetCatalog,
          elevationDrawingAnnotation,
        );
        break;
      }
      case "SECTION_VIEW": {
        const sectionMirrorTranslationX = getSectionViewMirrorTranslationX(parent.id, mirror, mirrorTranslationX);
        const mirroredDrawing = mirrorDrawingTemplateInstance({
          drawing,
          mirror,
          mirrorTranslationX: sectionMirrorTranslationX,
        });
        if (!mirroredDrawing || skipArchetypePlacement) {
          break;
        }
        addDrawingTemplateInstanceAnnotation(
          contentTemplateInstances,
          mirroredDrawing,
          instanceNode(),
          parent.id,
          handle,
          undefined,
          undefined,
          contentTemplateAnnotation,
          mirror,
          assetCatalog,
          sectionViewAnnotation,
        );
        break;
      }
      case "ROOM":
      case "ROOM_LAYOUT":
        // Use docRooms (not generated rooms) so component-member source rooms — which are
        // not generated standalone — still flow through to component instanced drawings below.
        if (!docRooms[parent.id] && !roomLayouts[parent.id]) {
          break;
        }

        let parentId = parent.id;
        if (parent.entityType === "ROOM_LAYOUT") {
          parentId = parent.parentId;
        }

        const mirroredDrawing = mirrorDrawingTemplateInstance({
          mirror,
          mirrorTranslationX: drawing.positioningMethod === PositioningMethod.EDGE ? 0 : mirrorTranslationX,
          drawing,
        });

        /**
         * Handle global positioned annotation, which will are not room layout eligible
         */
        if (drawing.positioningMethod === PositioningMethod.GLOBAL) {
          if (!skipArchetypePlacement && shouldGenerateEntity(parentId, idToParents, isolatedEntity)) {
            addDrawingTemplateInstanceAnnotation(
              contentTemplateInstances,
              mirroredDrawing,
              instanceNode(),
              parentId,
              handle,
              undefined,
              undefined,
              contentTemplateAnnotation,
              mirror,
              assetCatalog,
              roomAnnotation,
            );
          }
          break;
        }

        /**
         * Handle edge positioned annotation that could potentially be hosted on room layout instances within rooms.
         * Note that we're "double instancing" here - drawing template instances can be instanced via room layouts.
         */
        const drawingInstances = entityInstancesForEntity({
          ...roomLayoutInstancingOptions,
          entity: mirroredDrawing,
        });

        for (const instance of drawingInstances) {
          const drawingInstanceHandle: ContentTemplateInstanceHandle = {
            type: "CONTENT_TEMPLATE_INSTANCE",
            id: drawing.id,
          };
          if (!instance.edgeId) {
            issues.push({
              type: BuildingGenerationIssueType.INVALID_ANNOTATION_EDGE_ID,
              handle: drawingInstanceHandle,
              edgeId: "MISSING",
            });
            continue;
          }

          if (!instance.parentId) {
            continue;
          }

          const entityInstanceRoom = rooms[instance.parentId];

          const rliEnabledForArchetype = isRoomLayoutInstanceEnabledForChain(
            instance.handleOverride,
            [],
            roomLayoutInstances,
            { optionSets, instanceOptionSets },
            optionChoices,
          );

          /**
           * The source room may not be generated as a standalone room (e.g. when it is a
           * component member that only exists as component instances). In that case we skip
           * adding the drawing directly to the source room, but we still generate its
           * component instanced drawings below.
           */
          if (!skipArchetypePlacement && rliEnabledForArchetype && entityInstanceRoom) {
            const { handle, handleAuxiliary } = entityHandlesIfRoomLayoutInstanceOverride(
              drawingInstanceHandle,
              instance.handleOverride,
            );

            const id =
              instance.entityInstanceType === EntityInstanceType.ROOM_LAYOUT_INSTANCE
                ? `${instance.parentId}::${drawing.id}`
                : drawing.id;
            let drawingInstance: DrawingTemplateInstance = {
              ...mirroredDrawing,
              parentId: instance.parentId,
              edgeId: instance.edgeId ?? undefined,
              id,
            };

            const mirrorForDrawingInstance = isCombinedGlobalInstanceMirror(mirror, entityInstanceRoom);
            drawingInstance = drawingTemplateInstanceTransformToGlobal(
              drawingInstance,
              entityInstanceRoom,
              mirrorForDrawingInstance,
              issues,
            );
            addDrawingTemplateInstanceAnnotation(
              contentTemplateInstances,
              drawingInstance,
              instanceNode(),
              instance.parentId,
              handle,
              handleAuxiliary,
              instance.entityInstanceType,
              contentTemplateAnnotation,
              mirrorForDrawingInstance,
              assetCatalog,
              roomAnnotation,
            );
          }

          if (enabledFeatures.modelComponents && instance.parentId) {
            addComponentDrawings({
              drawing,
              target: {
                type: "ROOM",
                sourceId: instance.parentId,
                placement: {
                  edgeId: instance.edgeId,
                  handleOverride: instance.handleOverride,
                  sourceEntityInstanceType: instance.entityInstanceType ?? EntityInstanceType.ROOM_LAYOUT_SOURCE,
                },
                rooms,
                annotationTable: roomAnnotation,
              },
              instancingContext,
              instanceNode,
              contentTemplateInstances,
              contentTemplateAnnotation,
            });
          }
        }

        break;
      default:
        break;
    }
  }

  const autoGeneratedLevelAnnotation = generateLevelAnnotation(levels, rooms, planSettings, wallSettings);

  // resolve entity hosted annotations
  const buildingForEntityHostedAnnotations: BuildingForEntityHostedAnnotations = {
    rooms,
    roomIndex,
    wallMasses,
    mirror,
  };

  const resolvedRoomAnnotation = resolveEntityHostedAnnotationTableArray(
    buildingForEntityHostedAnnotations,
    roomAnnotation,
    wallLayerExtentsCache,
  );

  const resolvedAutoGeneratedLevelAnnotation = resolveEntityHostedAnnotationTableArray(
    buildingForEntityHostedAnnotations,
    autoGeneratedLevelAnnotation,
    wallLayerExtentsCache,
  );

  const resolvedUserDefinedLevelAnnotation = resolveEntityHostedAnnotationTableArray(
    buildingForEntityHostedAnnotations,
    userDefinedLevelAnnotation,
    wallLayerExtentsCache,
  );

  const resolvedElevationDrawingAnnotation = resolveEntityHostedAnnotationTableArray(
    buildingForEntityHostedAnnotations,
    elevationDrawingAnnotation,
    wallLayerExtentsCache,
  );

  const resolvedContentTemplateAnnotation = resolveEntityHostedAnnotationTableArray(
    buildingForEntityHostedAnnotations,
    contentTemplateAnnotation,
    wallLayerExtentsCache,
  );

  const resolvedContentTemplateInstanceAnnotation = resolveEntityHostedAnnotationTableArray(
    buildingForEntityHostedAnnotations,
    contentTemplateInstanceAnnotation,
    wallLayerExtentsCache,
  );

  const resolvedSectionViewAnnotation = resolveEntityHostedAnnotationTableArray(
    buildingForEntityHostedAnnotations,
    sectionViewAnnotation,
    wallLayerExtentsCache,
  );

  // Put all of the annotation into a single table for storing on the Doc
  const collectedAnnotation: RWTable<Annotation> = {};

  for (const id in resolvedRoomAnnotation) {
    resolvedRoomAnnotation[id].sort((a, b) => b.sortIndex - a.sortIndex);
    for (const anno of resolvedRoomAnnotation[id]) {
      collectedAnnotation[anno.id] = anno;
    }
  }

  for (const id in resolvedAutoGeneratedLevelAnnotation) {
    resolvedAutoGeneratedLevelAnnotation[id].sort((a, b) => b.sortIndex - a.sortIndex);
    for (const anno of resolvedAutoGeneratedLevelAnnotation[id]) {
      collectedAnnotation[anno.id] = anno;
    }
  }

  for (const id in resolvedUserDefinedLevelAnnotation) {
    resolvedUserDefinedLevelAnnotation[id].sort((a, b) => b.sortIndex - a.sortIndex);
    for (const anno of resolvedUserDefinedLevelAnnotation[id]) {
      collectedAnnotation[anno.id] = anno;
    }
  }

  for (const id in resolvedElevationDrawingAnnotation) {
    resolvedElevationDrawingAnnotation[id].sort((a, b) => b.sortIndex - a.sortIndex);
    for (const anno of resolvedElevationDrawingAnnotation[id]) {
      collectedAnnotation[anno.id] = anno;
    }
  }

  for (const id in resolvedSectionViewAnnotation) {
    resolvedSectionViewAnnotation[id].sort((a, b) => b.sortIndex - a.sortIndex);
    for (const anno of resolvedSectionViewAnnotation[id]) {
      collectedAnnotation[anno.id] = anno;
    }
  }

  for (const id in resolvedContentTemplateAnnotation) {
    for (const anno of resolvedContentTemplateAnnotation[id]) {
      collectedAnnotation[anno.id] = anno;
    }
  }

  for (const id in resolvedContentTemplateInstanceAnnotation) {
    for (const anno of resolvedContentTemplateInstanceAnnotation[id]) {
      collectedAnnotation[anno.id] = anno;
    }
  }

  return {
    issues,
    roomAnnotation: resolvedRoomAnnotation,
    levelAnnotation: {
      ...resolvedAutoGeneratedLevelAnnotation,
      ...resolvedUserDefinedLevelAnnotation,
    },
    elevationDrawingAnnotation: resolvedElevationDrawingAnnotation,
    sectionViewAnnotation: resolvedSectionViewAnnotation,
    annotation: collectedAnnotation,
    modelHostedDrawings: enabledContentTemplateInstances,
  };
}

export default memo(selectAnnotation, generateAnnotation, compare);
